import { useRef, useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { useUsuario } from '../../context/usuarioContext';
// Componentes UI
import { Toast } from 'primereact/toast';
import { Button } from 'primereact/button';
import { RadioButton } from 'primereact/radiobutton';
import { Checkbox } from 'primereact/checkbox';
// Componentes
import ModuleLayout from '../../layouts/layout-modulo.js';
// Datos
import { obtenerFlota } from '../../services/vehiculosService';
import { agregarCklSomnolencia, notificarAlerta, obtenerCklSomnolencia } from '../../services/conductoresService';
// Hooks y Validaciones
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { cklSomnolenciaSchema } from '../../validation/addCklSomnolencia';
// Utils
import { generarPDF } from "../../utils/pdf/pdfCklSomnolencia";
// Estilos y Assets
import logo from "../../assets/logos/TRANSKYRIOS-_IconColor.avif";
import "./ckl-somnolencia.css";

export default function CklSomnolencia() {
  const toast = useRef(null);
  const navigate = useNavigate();
  const btnVolverRef = useRef(null);
  const { usuario } = useUsuario();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [vehiculos, setVehiculos] = useState([]);

  const buildDefaultValues = (usuario) => ({
    conductor: `${usuario?.nombre || ''} ${usuario?.apellido_paterno || ''}`.trim(),
    id_usuario: usuario?.id_usuario || '',
    patente: '',
    id_vehiculo: '',
    // Sección I
    durmioBien: null,          // ¿Durmió bien/apto últimas 24h?
    medicacion: null,          // ¿Medicamentos que provocan somnolencia?
    medDetalle: '',            // medicacion = Sí, ¿cual?: txt
    actividadExigente: null,   // ¿Actividades físicas exigentes?
    resfriado: null,           // ¿Síntomas de resfriado común?
    comidasCopiosas: null,     // ¿Comidas copiosas última hora?
    // Sección II
    sintomas: {
      dificultadConcentrarse: null,
      movimientosLentos: null,
      visionBorroza: null,
      desorientacion: null,
      trayectoriaRecta: null,
      bostezos: null,
      parpadosPesados: null,
      cabeceos: null,
      dolorCabeza: null,
      mareos: null,
      dolorNucaEspalda: null,
      cambiosPostura: null,
    },
    // Consentimiento
    consentimiento: false,
  });

  const getVehiculos = useCallback(async () => {
    try {
      const res = await obtenerFlota();

      if (!Array.isArray(res)) throw new Error("Respuesta inesperada al obtener vehiculos");

      const map = {};
      res.forEach(v => {
        const patente = String(v.patente || "").toUpperCase().trim();
        if (!patente) return;
        map[patente] = { ...v, patente };
      });
      setVehiculos(Object.values(map));
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error || 'Ocurrió un error al cargar los vehículos.',
        life: 2500
      });
    }
  }, []);

  useEffect(() => { getVehiculos(); }, [getVehiculos]);

  const {
    register, handleSubmit, formState: { errors }, 
    control, reset, watch, setValue
  } = useForm({
    resolver: yupResolver(cklSomnolenciaSchema),
    context: { vehiculos },
    defaultValues: buildDefaultValues(usuario),
    mode: 'onTouched', reValidateMode: 'onChange',
  });

  const limpiarForm = () => reset(buildDefaultValues(usuario));

  const enviarChecklist = async (data) => {
    setIsSubmitting(true);

    // Si medicacion se marco como "No" se borra el detalle.
    if (data.medicacion === false) data.medDetalle = '';

    try {
      const { sintomas, ...restoData } = data;
      
      const payload = {
        cabecera: {...restoData},
        detalle: sintomas
      };
      
      const resp = await agregarCklSomnolencia(payload);

      if (resp?.ok === false) {
        throw new Error('No se pudo registrar el checklist');
      }

      toast.current?.show({
        severity: 'success',
        summary: 'Checklist enviado',
        detail: 'Se registró su evaluación correctamente.',
        sticky: true
      });

      // --------------- Notificaciones -------------
      const nivelAlerta = resp?.estado_alerta?.det_estado;
      const tipoChecklist = resp?.tipoChecklist;
      const cod_checklist = resp?.cod_checklist;
      const id_checklist = resp?.id_checklist;

      // UI de alertas
      mostrarAlertasUI(nivelAlerta);
      
      if (nivelAlerta !== 'OK') {
        try {
          await notificarAlertaChecklist({ id_checklist, cod_checklist, nivelAlerta, tipoChecklist });
          toast.current?.show({
            severity: 'info',
            summary: 'Notificación enviada',
            detail: 'Se ha notificado a la supervisión.',
            sticky: true
          });
        } catch (error) {
          toast.current?.show({
            severity: 'warn',
            summary: 'Checklist registrado',
            detail: error || 'No se pudo notificar al supervisor por correo.',
            sticky: true
          });
        }
      }

      // Limpia el formulario
      limpiarForm();
      // Enfoca al botón volver
      btnVolverRef.current?.focus?.(); 
    
    } catch (error) {
      toast.current?.show({
        severity: 'error',
        summary: 'Error',
        detail: error || 'Ocurrió un error al enviar el checklist. Intente nuevamente.',
        life: 3500
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const notificarAlertaChecklist = async ({ id_checklist, cod_checklist, nivelAlerta, tipoChecklist }) => {
    try {
      // Obtener datos estructurados del checklist
      const data = await obtenerCklSomnolencia(id_checklist);
      if (!data) throw new Error('No se recibieron datos');
      
      // Generar Blob(PDF) a partir de la data estructurada
      const pdf = await new Promise((resolve) => {
        generarPDF(data).getBlob((blob) => { resolve(blob); });
      });

      // Adjuntar datos para el correo
      const formData = new FormData();
      formData.append('checklist', pdf, `checklist_${cod_checklist}.pdf`);
      formData.append('nivelAlerta', nivelAlerta);
      formData.append('tipoChecklist', tipoChecklist);
      formData.append('conductor', data.cabecera.conductor);
      formData.append('patente', data.cabecera.patente);

      // Enviar correo
      const resp = await notificarAlerta(cod_checklist, formData);

      return resp;
    } catch (error) {
      throw error;
    }
  };

  const mostrarAlertasUI = (nivelAlerta) => {
    if (nivelAlerta === 'OK') {
      toast.current?.show({
        severity: 'success',
        summary: 'Apto para conducir',
        detail: 'Se encuentra apto para conducir. ¡Que tenga buen viaje!',
        sticky: true
      });
    }

    if (nivelAlerta === 'ALERTA_CRITICA') {
      toast.current?.show({
        severity: 'error',
        summary: 'Alerta crítica de somnolencia',
        detail: 'Debe ser evaluado de inmediato.',
        sticky: true
      });
      toast.current?.show({
        severity: 'info',
        summary: 'Instrucciones',
        detail: 'Por favor, contacte a su supervisor o al área de prevención para recibir instrucciones adicionales.',
        sticky: true
      });
    }

    if (nivelAlerta === 'ALERTA_MEDIA') {
      toast.current?.show({
        severity: 'warn',
        summary: 'Alerta moderada de somnolencia',
        detail: 'Se recomienda tomar un descanso antes de continuar su viaje.',
        sticky: true
      });
      toast.current?.show({
        severity: 'info',
        summary: 'Recomendación',
        detail: 'Considere detenerse en un lugar seguro para descansar y rehidratarse.',
        sticky: true
      });
    }
  };

  // Helper para errores anidados
  const getError = (errorsObj, path) =>
    path.split('.').reduce((o, k) => (o ? o[k] : undefined), errorsObj);

  // Fila Sí | No 
  const RowSiNo = ({ name, label }) => {
    const fieldError = getError(errors, name);
    // const val = watch(name);

    return (
      <div className="sino-bloque">
      <div className="sino-row">
        {/* Pregunta */}
        <div className="sino-question">
          <label className="form-label mb-0">{label}</label>
        </div>
        {/* Botones Sí / No */}
        <div className="sino-options">
          <div className="option">
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <RadioButton
                  inputId={`${name}-si`}
                  value={true}
                  checked={field.value === true}
                  onChange={(e) => field.onChange(e.value)}
                />
              )}
            />
            <label htmlFor={`${name}-si`} className="mb-0">Sí</label>
          </div>
          <div className="option">
            <Controller
              name={name}
              control={control}
              render={({ field }) => (
                <RadioButton
                  inputId={`${name}-no`}
                  value={false}
                  checked={field.value === false}
                  onChange={(e) => field.onChange(e.value)}
                />
              )}
            />
            <label htmlFor={`${name}-no`} className="mb-0">No</label>
          </div>
        </div>
        
      </div>
        {/* Error */}
        {fieldError && (
          <div className="invalid-feedback d-block mb-1">
            {fieldError.message || 'Seleccione una opción'}
          </div>
        )}
      </div>
    );
  };

  const cambiarVehiculo = (patente) => {
    const normalizada = (patente || "").trim().toUpperCase();
    
    const vehiculo = vehiculos.find(
      v => v.patente.toUpperCase() === normalizada
    );

    if (vehiculo) {
      setValue('id_vehiculo', vehiculo.id_vehiculo);
    } else {
      setValue('id_vehiculo', '');
    }
  };

  return (
    <ModuleLayout titulo="Panel del Conductor" logo={logo} onVolver={() => navigate('/app/panel-conductor')}>
      <Toast ref={toast} position='center'/>
      <section className="tmc-content mb-4">
        <h3 className='titulo-form'>Checklist Fatiga y Somnolencia</h3>
        <section className="form-section">
          <form className="container-form">
            <div className='card-nc'>
              <h5>Identificación</h5>
                {/* Conductor*/}
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Conductor/a</label>
                  <input type="text" className='form-control' {...register("conductor")} readOnly/>
                </div>
                {/* Patente */}
                <div className="mb-2 form-comprobante-field">
                  <label className="form-label">Patente <span className="required">*</span></label>

                  <Controller
                    name="patente"
                    control={control}
                    render={({ field }) => (
                      <>
                        <input
                          {...field}
                          id="patenteInput"
                          className={
                            `form-control text-uppercase 
                            ${ !field.value ? "border-warning bg-warning-subtle" : "border-success"} 
                            ${ errors.patente ? 'is-invalid' : '' }`
                          }
                          placeholder="Ingrese Patente"
                          list="vehiculosList"
                          autoComplete="off"
                          spellCheck={false}
                          onChange={(e) => {
                            const patente = e.target.value.toUpperCase();
                            field.onChange(patente);
                            cambiarVehiculo(patente);
                          }}
                        />
                        <datalist id="vehiculosList">
                          {vehiculos.map(v => (
                            <option key={v.id_vehiculo} value={v.patente} />
                          ))}
                        </datalist>
                        {errors.patente && (
                          <div className="invalid-feedback d-block">
                            {errors.patente.message}
                          </div>
                        )}
                      </>
                    )}
                  />
                </div>
            </div>
          
            {/* I. Preguntas */}
            <div className='card-nc'>
              <h5>I. Responda a las preguntas</h5>

              <RowSiNo
                name="durmioBien"
                label="1. Confirma que ha dormido el tiempo necesario en las últimas 24 horas y se encuentra apto/a para conducir."
              />
              <RowSiNo
                name="medicacion"
                label="2. ¿Actualmente está tomando medicamentos que provocan somnolencia o sensación de fatiga?"
              />
              {watch('medicacion') === true && (
                <div className="form-comprobante-row">
                  <div className="mb-2 form-comprobante-field" style={{ flex: 1 }}>
                    <label className="form-label">Si respondió Sí, ¿Cuál?</label>
                    <input type="text" className="form-control" placeholder='Nombre del medicamento ' {...register('medDetalle')} />
                    {errors?.medDetalle && (
                      <div className="invalid-feedback d-block">{errors.medDetalle.message}</div>
                    )}
                  </div>
                </div>
              )}
              <RowSiNo name="actividadExigente" label="3. ¿Ha realizado actividades físicas exigentes o prolongadas antes de conducir?" />
              <RowSiNo name="resfriado" label="4. ¿Presenta síntomas de resfriado común?" />
              <RowSiNo name="comidasCopiosas" label="5. ¿Ha ingerido una comida abundante o pesada en la última hora?" />
            </div>

            {/* II. Síntomas última media hora */}
            <div className='card-nc'>
              <h5>II. Durante la última media hora, ¿ha presentado alguno de estos síntomas?</h5>
              <RowSiNo name="sintomas.dificultadConcentrarse" label="Dificultad para concentrarse o permanecer alerta" />
              <RowSiNo name="sintomas.movimientosLentos" label="Movimientos lentos o torpes" />
              <RowSiNo name="sintomas.visionBorroza" label="Visión borrosa" />
              <RowSiNo name="sintomas.desorientacion" label="Dificultad para recordar cómo se alcanzó la localización actual" />
              <RowSiNo name="sintomas.trayectoriaRecta" label="Dificultad para mantener una trayectoria recta" />
              <RowSiNo name="sintomas.bostezos" label="Bostezos frecuentes" />
              <RowSiNo name="sintomas.parpadosPesados" label="Pesadez en los párpados" />
              <RowSiNo name="sintomas.cabeceos" label='Muchas ganas de dormir que impiden concentrarse ("cabeceos")' />
              <RowSiNo name="sintomas.dolorCabeza" label="Dolor de cabeza" />
              <RowSiNo name="sintomas.mareos" label="Sensación de mareos" />
              <RowSiNo name="sintomas.dolorNucaEspalda" label="Dolores de nuca y espalda que hacen incómoda la conducción" />
              <RowSiNo name="sintomas.cambiosPostura" label="Cambios de postura frecuentes, estiramientos o acomodos" />
            </div>

            {/* Consentimiento */}
            <div className="card-nc">
              <h5>Declaración de consentimiento</h5>
              <p className="text-muted" style={{ textAlign: 'justify' }}>
                Declaro bajo mi responsabilidad que la información registrada en el presente Checklist de Evaluación de Fatiga y Somnolencia
                es verídica y corresponde a mi condición real al momento de la evaluación. Me hago plenamente responsable de la veracidad de
                los datos entregados, entendiendo que cualquier omisión o falsedad puede afectar la seguridad propia y de terceros, así como
                derivar en medidas disciplinarias conforme a la normativa interna de la empresa y la legislación vigente.
              </p>

              <div className="form-comprobante-row gap-0">
                <div className="mb-2 form-comprobante-field d-flex align-items-center gap-2">
                  <Controller
                    name="consentimiento"
                    control={control}
                    render={({ field }) => (
                      <Checkbox
                        inputId="consentimiento"
                        checked={!!field.value}
                        onChange={(e) => field.onChange(e.checked)}
                      />
                    )}
                  />
                  <label htmlFor="consentimiento" className="mb-0">Acepto la declaración anterior.</label>
                </div>
                {errors?.consentimiento && (
                  <div className="invalid-feedback d-block">
                    {errors.consentimiento.message}
                  </div>
                )}
              </div>
            </div>

            <div className='tmc-btns-container'>
              <Button type="button" label="Guardar" icon="pi pi-save" 
                className="inv-btn-confirm"
                onClick={handleSubmit(enviarChecklist)}
                disabled={isSubmitting}
              />
            </div>
          </form>
        </section>
      </section>
    </ModuleLayout>
  );
}